<script lang="ts">
  /**
   * Whose turn it is, on one device (1.7.0): the moment between two players
   * taking turns on the same phone, tablet or computer. "Over to Noor", what
   * is at stake, and one wide button the next player presses themselves:
   * "I am Noor". TwinTen's race in turns, Interlock's runs in turn and
   * Tilestorm's pass-and-play hand-over card are each one of these.
   *
   * Two presentations:
   *
   * - `screen` (the default): a full-screen dialog over the game, the
   *   native `<dialog>` with `showModal()` (the browser keeps the focus
   *   inside and the page behind inert). `conceal` (default `true`) draws it
   *   on the opaque ground, so nothing of what is behind it shows (the next
   *   pieces, a rack, a board the next player must not see yet); `false`
   *   lays a card over the scrim and the game stays in view behind it.
   * - `card`: in the page, where the game puts it (Tilestorm: in
   *   `GameFrame`'s `bottom`, in place of the rack and the tools, the board
   *   left in view). No dialog and no history entry of its own: the game
   *   screen keeps its Back.
   *
   * What it guarantees:
   *
   * - **Focus and announce.** When it shows (and whenever `name` changes
   *   while it shows) focus moves to the title, so a screen reader says
   *   "Over to Noor" and a keyboard starts there; Tab reaches the button.
   *   The screen is a dialog named by the title and described by `detail`.
   * - **No stray tap.** The ready button ignores presses for `armDelayMs`
   *   (default 500) after it shows, while it says `aria-disabled`: a tap that
   *   was meant for the board as the device changed hands does nothing.
   * - **Never waved away.** The backdrop, Escape and Back never skip a
   *   player or dismiss it, and the screen always has a way out (the types
   *   require `back` or `onbackattempt` unless `presentation` is `card`). With `back` (a quiet word under the button: "Back
   *   to the setup", "Pause"), Escape and Back call `back.onclick`, as the
   *   word does; without it they call `onbackattempt` with what they were.
   *   As a non-dismissible `Sheet` does, Back lands on the screen's own
   *   history entry, which is armed again at once, so a Back never leaves the
   *   app from under it. The game takes it down (`open` false, or unmounts it).
   *
   * Top to bottom: `eyebrow` ("Stage 2 of 5"), a mark, `title` (default the
   * shell's "Over to {name}"), `detail`, `standings` (compact by default),
   * the game's own `children`, the ready button (`readyLabel`, default
   * "I am {name}", full width) and `back`.
   *
   * Test ids: `testid` (default `ui-hand-off`) on the dialog or the card,
   * with `data-armed`; `testids.title` (default `ui-hand-off-title`),
   * `testids.ready` (default `ui-hand-off-ready`), `testids.standings`
   * (default `ui-hand-off-standings`); `back` takes its own (default
   * `ui-hand-off-back`).
   */
  import type { Snippet } from 'svelte';

  import { pushOverlay } from '../historyStack.js';
  import { shellText } from '../i18n.svelte.js';
  import type { StandingsBlock } from '../templates/playTypes.js';
  import type { SheetBackAttempt, TemplateAction } from '../types.js';
  import Icon from './Icon.svelte';
  import Standings from './Standings.svelte';
  import TextButton from './TextButton.svelte';

  interface CommonProps {
    /** Whether it shows. Default `true`: a game may also mount it only while it shows. */
    readonly open?: boolean;
    /** Whose turn it is: the default title and button say it. */
    readonly name: string;
    readonly title?: string | undefined;
    readonly eyebrow?: string | undefined;
    readonly detail?: string | undefined;
    readonly standings?: StandingsBlock | undefined;
    readonly children?: Snippet | undefined;
    /** The next player is holding the device: the turn starts. */
    readonly onready: () => void;
    readonly readyLabel?: string | undefined;
    /** `screen` only: the opaque ground (default) or a card over the scrim. */
    readonly conceal?: boolean;
    /** How long the ready button ignores presses after it shows, in ms. Default 500. */
    readonly armDelayMs?: number;
    readonly testid?: string | undefined;
    readonly testids?:
      { readonly title?: string; readonly ready?: string; readonly standings?: string } | undefined;
  }

  /**
   * A way out, always, on the screen (1.7.0 review): `back` (the quiet word,
   * which Escape and Back call too), or `onbackattempt` to hear them. A card
   * in the page keeps the game screen's own Back, so it needs neither.
   */
  type BackAttempt = (reason: SheetBackAttempt) => void;
  type WayOut =
    | {
        readonly presentation: 'card';
        readonly back?: TemplateAction | undefined;
        readonly onbackattempt?: BackAttempt | undefined;
      }
    | {
        readonly presentation?: 'screen' | undefined;
        /** The quiet way out (to the game's setup or menu); Escape and Back call it too. */
        readonly back: TemplateAction;
        /** Escape, Back or the backdrop, when there is no `back`. */
        readonly onbackattempt?: BackAttempt | undefined;
      }
    | {
        readonly presentation?: 'screen' | undefined;
        readonly back?: undefined;
        readonly onbackattempt: BackAttempt;
      };

  type Props = CommonProps & WayOut;
  const {
    open = true,
    name,
    title,
    eyebrow,
    detail,
    standings,
    children,
    onready,
    readyLabel,
    back,
    onbackattempt,
    presentation = 'screen',
    conceal = true,
    armDelayMs = 500,
    testid = 'ui-hand-off',
    testids,
  }: Props = $props();

  const titleId = $props.id();
  const detailId = `${titleId}-detail`;
  const screen = $derived(presentation === 'screen');
  const shownTitle = $derived(title ?? shellText('handOffTitle', { name }));
  const shownReady = $derived(readyLabel ?? shellText('handOffReady', { name }));

  let dialog = $state<HTMLDialogElement | null>(null);
  let heading = $state<HTMLElement | null>(null);
  // svelte-ignore state_referenced_locally
  let armed = $state(armDelayMs <= 0);

  /** One way out that is not the ready button: the game's `back`, or the attempt reported. */
  function leave(reason: SheetBackAttempt): void {
    if (back !== undefined && back.disabled !== true && back.busy !== true) back.onclick();
    else onbackattempt?.(reason);
  }

  function ready(): void {
    if (!armed) return;
    onready();
  }

  /* ---------------------------------------------------------------- *
   * The screen: a native modal dialog, as `Sheet` keeps one
   * ---------------------------------------------------------------- */

  function showNative(element: HTMLDialogElement): void {
    if (typeof element.showModal === 'function') {
      try {
        element.showModal();
        return;
      } catch {
        // Not connected yet, or already open non-modally: fall through.
      }
    }
    element.setAttribute('open', '');
  }

  // Our own `close()` fires a queued `close` event; each is counted and
  // swallowed (the bookkeeping `Sheet` keeps), so only a close the browser
  // made by itself is answered, by showing it again.
  let ownCloses = 0;
  let destroyed = false;
  $effect(() => () => {
    destroyed = true;
  });

  function hideNative(element: HTMLDialogElement): void {
    if (typeof element.close === 'function' && element.open) {
      ownCloses += 1;
      element.close();
    } else element.removeAttribute('open');
  }

  $effect(() => {
    const element = dialog;
    if (!screen || element === null || !open) return undefined;
    const previouslyFocused = document.activeElement;
    showNative(element);
    heading?.focus({ preventScroll: true });
    return () => {
      hideNative(element);
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  });

  // Each time it shows, and each time the turn passes to somebody else while
  // it shows: disarmed for a moment, and focus on the title.
  $effect(() => {
    void name;
    const target = heading;
    if (!open || target === null) return undefined;
    target.focus({ preventScroll: true });
    if (armDelayMs <= 0) {
      armed = true;
      return undefined;
    }
    armed = false;
    const timer = setTimeout(() => {
      armed = true;
    }, armDelayMs);
    return () => clearTimeout(timer);
  });

  // One history entry while the screen shows. Back is never a dismissal: the
  // entry is armed again at once and the attempt goes to `leave`, read when
  // Back arrives (a fresh `back` from the parent re-pushes nothing).
  $effect(() => {
    if (!screen || !open) return undefined;
    let release: (() => void) | null = null;
    const arm = (): void => {
      release = pushOverlay(() => {
        release = null;
        arm();
        leave('back');
      });
    };
    arm();
    return () => release?.();
  });

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    leave('escape');
  }

  /** The browser's own close request (Escape it handled, or Android's Back). */
  function onCancel(event: Event): void {
    event.preventDefault();
    leave('back');
  }

  function onNativeClose(): void {
    if (ownCloses > 0) {
      ownCloses -= 1;
      return;
    }
    if (destroyed || dialog === null || !dialog.isConnected || !open) return;
    // Not the browser's to close: show it again.
    showNative(dialog);
    leave('back');
  }

  function onBackdropPointerDown(event: Event): void {
    // Only reachable with `conceal` false: the panel is a child, so a press
    // whose target is the dialog itself missed it. Reported, never a close.
    if (event.target === dialog) onbackattempt?.('backdrop');
  }
</script>

{#snippet body()}
  {#if eyebrow !== undefined}<p class="ui-handoff-eyebrow">{eyebrow}</p>{/if}
  {#if screen}
    <span class="ui-handoff-mark" aria-hidden="true"><Icon name="person" size={28} /></span>
  {/if}
  <h2
    class="ui-handoff-title"
    id={titleId}
    tabindex="-1"
    bind:this={heading}
    data-testid={testids?.title ?? 'ui-hand-off-title'}
  >
    {shownTitle}
  </h2>
  {#if detail !== undefined}<p class="ui-handoff-detail" id={detailId}>{detail}</p>{/if}
  {#if standings !== undefined}
    <div class="ui-handoff-standings">
      <Standings
        rows={standings.rows}
        label={standings.label}
        winnerLabel={standings.winnerLabel}
        formatRank={standings.formatRank}
        size={standings.size ?? 'compact'}
        testid={standings.testid ?? testids?.standings ?? 'ui-hand-off-standings'}
      />
    </div>
  {/if}
  {#if children !== undefined}{@render children()}{/if}
  <div class="ui-handoff-actions">
    <button
      type="button"
      class="ui-handoff-ready"
      aria-disabled={armed ? undefined : 'true'}
      data-testid={testids?.ready ?? 'ui-hand-off-ready'}
      onclick={ready}
    >
      {shownReady}
    </button>
    {#if back !== undefined}
      <TextButton
        label={back.label}
        onclick={back.onclick}
        disabled={back.disabled ?? false}
        icon={back.icon}
        testid={back.testid ?? 'ui-hand-off-back'}
      />
    {/if}
  </div>
{/snippet}

{#if screen}
  <dialog
    bind:this={dialog}
    class="ui-handoff"
    class:ui-handoff--conceal={conceal}
    aria-labelledby={titleId}
    aria-describedby={detail === undefined ? undefined : detailId}
    aria-modal="true"
    data-testid={testid}
    data-armed={armed ? 'true' : 'false'}
    onkeydown={onKeydown}
    oncancel={onCancel}
    onclose={onNativeClose}
    onpointerdown={onBackdropPointerDown}
  >
    {#if open}
      <div class="ui-handoff-scroll">
        <div class="ui-handoff-panel">{@render body()}</div>
      </div>
    {/if}
  </dialog>
{:else if open}
  <section
    class="ui-handoff-card"
    aria-labelledby={titleId}
    aria-describedby={detail === undefined ? undefined : detailId}
    data-testid={testid}
    data-armed={armed ? 'true' : 'false'}
  >
    {@render body()}
  </section>
{/if}

<style>
  /* `display` is load-bearing, as on `Sheet`: a closed <dialog> must keep
     the user agent's `display: none`, or it covers the page and eats taps. */
  .ui-handoff {
    position: fixed;
    inset: 0;
    margin: 0;
    padding: 0;
    border: 0;
    width: 100%;
    max-width: none;
    height: 100dvh;
    max-height: 100dvh;
    display: flex;
    overflow: hidden;
    background: transparent;
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-handoff:not([open]) {
    display: none;
  }

  .ui-handoff::backdrop {
    background: var(--ui-scrim);
  }

  /* Concealed: the opaque ground, edge to edge, so nothing behind shows. */
  .ui-handoff--conceal {
    background: var(--ui-ground);
  }

  .ui-handoff--conceal::backdrop {
    background: var(--ui-ground);
  }

  .ui-handoff-scroll {
    box-sizing: border-box;
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: calc(var(--ui-space-5) + env(safe-area-inset-top))
      calc(var(--ui-space-4) + env(safe-area-inset-right))
      calc(var(--ui-space-5) + env(safe-area-inset-bottom))
      calc(var(--ui-space-4) + env(safe-area-inset-left));
  }

  .ui-handoff-panel {
    box-sizing: border-box;
    width: min(26rem, 100%);
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--ui-space-3);
    text-align: center;
  }

  /* Over the game: a card on the scrim. */
  .ui-handoff:not(.ui-handoff--conceal) .ui-handoff-panel {
    padding: var(--ui-space-5);
    border-radius: var(--ui-radius-lg);
    background: var(--ui-surface);
  }

  .ui-handoff-card {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--ui-space-2);
    margin: var(--ui-space-2) var(--ui-space-3);
    padding: var(--ui-space-4);
    border-radius: var(--ui-radius-lg);
    background: var(--ui-surface);
    color: var(--ui-ink);
    font-family: var(--ui-font);
    text-align: center;
  }

  .ui-handoff-eyebrow {
    margin: 0;
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
    color: var(--ui-ink-2);
  }

  .ui-handoff-mark {
    width: 3.5rem;
    height: 3.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--ui-surface-2);
    color: var(--ui-accent);
  }

  .ui-handoff-title {
    margin: 0;
    font: var(--ui-text-title);
    color: var(--ui-ink);
    outline: none;
    overflow-wrap: anywhere;
  }

  .ui-handoff-card .ui-handoff-title {
    font: var(--ui-text-heading);
  }

  .ui-handoff-detail {
    margin: 0;
    font: var(--ui-text-body);
    color: var(--ui-ink-2);
  }

  .ui-handoff-standings {
    align-self: stretch;
    margin-top: var(--ui-space-1);
  }

  .ui-handoff-actions {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--ui-space-1);
    margin-top: var(--ui-space-2);
  }

  .ui-handoff-ready {
    min-height: 3.1rem;
    padding: 0.85rem 1.5rem;
    border: 0;
    border-radius: 999px;
    background: var(--ui-accent);
    color: var(--ui-on-accent);
    font: var(--ui-text-body);
    font-weight: 800;
    overflow-wrap: anywhere;
  }

  .ui-handoff-ready[aria-disabled='true'] {
    cursor: default;
  }

  .ui-handoff-ready:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: no-preference) {
    .ui-handoff[open] .ui-handoff-panel,
    .ui-handoff-card {
      animation: ui-handoff-in 0.2s ease-out;
    }
  }

  @keyframes ui-handoff-in {
    from {
      transform: translateY(8px);
      opacity: 0.4;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }
</style>
